import React, { useRef, useState } from "react";
import "./App.css";
import MainLogo from "./brand/MainLogo";
import RonaWalkLogo from "./brand/RonaWalkLogo";
import MicroIcon from "./brand/MicroIcon";
import WalkFigureIcon from "./brand/WalkFigureIcon";
import TreeIcon from "./brand/TreeIcon";
import SignatureRW from "./brand/SignatureRW";
import PillarSymbol from "./brand/PillarSymbol";
import GoldenPathLogo from "./brand/GoldenPathLogo";
import { EtsyBanner, ShopifyHero, YouTubeBanner, InstagramPost, InstagramStory, FacebookCover, YouTubeThumbnail } from "./brand/Banners";
import { MerchPrint, ShopIcon } from "./brand/PODAssets";
import { COLORS, TAGLINE, PILLARS, TAGLINE_HIERARCHY, SEASONAL_PALETTES, STORY_WELLNESS, STORY_WALK, BRAND_MISSION, BRAND_VISION, BRAND_VALUES } from "./brand/tokens";
import { downloadSVG, downloadPNG, downloadKit } from "./brand/download";
import { generateDesignerBrief } from "./brand/designerBrief";

const Swatch = ({ label, hex, dark }) => (
  <div className="swatch" data-testid={`swatch-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="swatch-tile" style={{ background: hex, color: dark ? "#fff" : COLORS.ink }}>
      <span>{hex.toUpperCase()}</span>
    </div>
    <div className="swatch-label">{label}</div>
  </div>
);

const AssetCard = ({ title, description, children, actions, tall }) => (
  <div className={`asset ${tall ? "asset--tall" : ""}`} data-testid={`asset-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="asset__stage">{children}</div>
    <div className="asset__meta">
      <h3>{title}</h3>
      <p>{description}</p>
      <div className="asset__actions">{actions}</div>
    </div>
  </div>
);

const Btn = ({ children, onClick, variant = "gold", testId }) => (
  <button className={`btn btn--${variant}`} onClick={onClick} data-testid={testId}>
    {children}
  </button>
);

export default function BrandHub() {
  // refs to each SVG so we can serialize/download
  const refs = {
    main: useRef(null),
    walk: useRef(null),
    micro: useRef(null),
    sig: useRef(null),
    pillar: useRef(null),
    goldenPath: useRef(null),
    etsy: useRef(null),
    shopify: useRef(null),
    youtube: useRef(null),
    youtubeThumb: useRef(null),
    instagram: useRef(null),
    igStory: useRef(null),
    facebook: useRef(null),
    merch: useRef(null),
    shopIcon: useRef(null),
    sigBlack: useRef(null),
    sigWhite: useRef(null),
    microGold: useRef(null),
    walkFigure: useRef(null),
    walkFigureSage: useRef(null),
    treeIconSage: useRef(null),
    treeIconGold: useRef(null),
    treeIconNoPath: useRef(null),
  };

  const [busy, setBusy] = useState(false);
  const [briefBusy, setBriefBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const announce = (msg) => {
    setToast(msg);
    window.clearTimeout(announce._t);
    announce._t = window.setTimeout(() => setToast(null), 2600);
  };

  const handleDownloadAll = async () => {
    setBusy(true);
    try {
      await downloadKit(
        [
          {
            el: refs.main.current,
            name: "rona-wellness-main-logo",
            png: [
              { w: 2000, h: 2000, suffix: "2000" },
              { w: 4500, h: 4500, suffix: "4500-square" },
              { w: 1024, h: 1024, suffix: "1024" },
              { w: 512, h: 512, suffix: "512" },
            ],
          },
          {
            el: refs.walk.current,
            name: "the-rona-walk-logo",
            png: [
              { w: 2000, h: 1050, suffix: "2000" },
              { w: 4500, h: 2362, suffix: "4500" },
            ],
          },
          {
            el: refs.micro.current,
            name: "micro-icon-rw-feather-sage",
            png: [
              { w: 1024, h: 1024, suffix: "1024" },
              { w: 2000, h: 2000, suffix: "2000" },
            ],
          },
          {
            el: refs.microGold.current,
            name: "micro-icon-rw-feather-gold",
            png: [
              { w: 1024, h: 1024, suffix: "1024" },
              { w: 2000, h: 2000, suffix: "2000" },
            ],
          },
          {
            el: refs.walkFigure.current,
            name: "walk-figure-icon-gold",
            png: [
              { w: 1024, h: 1024, suffix: "1024" },
              { w: 2000, h: 2000, suffix: "2000" },
              { w: 4500, h: 4500, suffix: "4500-embroidery" },
            ],
          },
          {
            el: refs.walkFigureSage.current,
            name: "walk-figure-icon-sage",
            png: [
              { w: 1024, h: 1024, suffix: "1024" },
              { w: 2000, h: 2000, suffix: "2000" },
            ],
          },
          {
            el: refs.treeIconSage.current,
            name: "tree-sage-goldpath",
            png: [
              { w: 1024, h: 1024, suffix: "1024" },
              { w: 2000, h: 2000, suffix: "2000" },
              { w: 4500, h: 4500, suffix: "4500-embroidery" },
            ],
          },
          {
            el: refs.treeIconGold.current,
            name: "tree-gold-brownpath",
            png: [
              { w: 1024, h: 1024, suffix: "1024" },
              { w: 2000, h: 2000, suffix: "2000" },
            ],
          },
          {
            el: refs.treeIconNoPath.current,
            name: "tree-icon-only-sage",
            png: [
              { w: 1024, h: 1024, suffix: "1024" },
              { w: 2000, h: 2000, suffix: "2000" },
            ],
          },
          {
            el: refs.sig.current,
            name: "signature-rw-gold",
            png: [
              { w: 2000, h: 1111, suffix: "2000" },
              { w: 4500, h: 2500, suffix: "4500" },
            ],
          },
          {
            el: refs.sigBlack.current,
            name: "signature-rw-black",
            png: [{ w: 2000, h: 1111, suffix: "2000" }],
          },
          {
            el: refs.sigWhite.current,
            name: "signature-rw-white",
            png: [{ w: 2000, h: 1111, suffix: "2000" }],
          },
          {
            el: refs.pillar.current,
            name: "pillar-symbol-faith-strength-hope",
            png: [
              { w: 800, h: 1000, suffix: "800" },
              { w: 1600, h: 2000, suffix: "1600" },
            ],
          },
          {
            el: refs.goldenPath.current,
            name: "golden-path-logo",
            png: [
              { w: 2000, h: 1250, suffix: "2000" },
              { w: 4500, h: 2812, suffix: "4500" },
            ],
          },
          {
            el: refs.etsy.current,
            name: "banner-etsy-3360x840",
            png: [{ w: 3360, h: 840, suffix: "3360x840" }],
          },
          {
            el: refs.shopify.current,
            name: "banner-shopify-1920x1080",
            png: [{ w: 1920, h: 1080, suffix: "1920x1080" }],
          },
          {
            el: refs.youtube.current,
            name: "banner-youtube-cover-2560x1440",
            png: [{ w: 2560, h: 1440, suffix: "2560x1440" }],
          },
          {
            el: refs.youtubeThumb.current,
            name: "banner-youtube-thumbnail-1280x720",
            png: [{ w: 1280, h: 720, suffix: "1280x720" }],
          },
          {
            el: refs.instagram.current,
            name: "banner-instagram-post-1080x1080",
            png: [{ w: 1080, h: 1080, suffix: "1080x1080" }],
          },
          {
            el: refs.igStory.current,
            name: "banner-instagram-story-1080x1920",
            png: [{ w: 1080, h: 1920, suffix: "1080x1920" }],
          },
          {
            el: refs.facebook.current,
            name: "banner-facebook-cover-1640x924",
            png: [{ w: 1640, h: 924, suffix: "1640x924" }],
          },
          {
            el: refs.merch.current,
            name: "pod-amazon-merch-tee-4500x5400",
            png: [{ w: 4500, h: 5400, suffix: "4500x5400" }],
          },
          {
            el: refs.shopIcon.current,
            name: "pod-etsy-shop-icon-500x500",
            png: [
              { w: 500, h: 500, suffix: "500" },
              { w: 1000, h: 1000, suffix: "1000" },
            ],
          },
        ],
        "rona-wellness-brand-kit.zip"
      );
      announce("Full brand kit downloaded ✓");
    } catch (e) {
      console.error(e);
      announce("Download failed — try again");
    } finally {
      setBusy(false);
    }
  };

  const dlSvg = (ref, name) => () => {
    downloadSVG(ref.current, `${name}.svg`);
    announce(`${name}.svg downloaded`);
  };
  const dlPng = (ref, name, w, h) => async () => {
    await downloadPNG(ref.current, `${name}-${w}x${h}.png`, w, h);
    announce(`${name}-${w}x${h}.png downloaded`);
  };

  const handleDownloadBrief = async () => {
    setBriefBusy(true);
    try {
      await generateDesignerBrief({
        mainLogoEl: refs.main.current,
        walkLogoEl: refs.walk.current,
        microEl: refs.micro.current,
        sigEl: refs.sig.current,
        pillarEl: refs.pillar.current,
      });
      announce("Designer brief PDF downloaded ✓");
    } catch (e) {
      console.error(e);
      announce("Brief generation failed — try again");
    } finally {
      setBriefBusy(false);
    }
  };


  return (
    <div className="hub" data-testid="brand-hub">
      {/* Toast */}
      {toast && (
        <div className="toast" data-testid="toast">{toast}</div>
      )}

      {/* ============ HERO ============ */}
      <header className="hero">
        <div className="hero__grain" aria-hidden />
        <div className="hero__ring" aria-hidden />

        <nav className="nav">
          <div className="nav__mark">
            <MicroIcon size={28} color={COLORS.gold} />
            <span>Rona Wellness</span>
          </div>
          <div className="nav__links">
            <a href="#story" data-testid="nav-story">Story</a>
            <a href="#mvv" data-testid="nav-mvv">Values</a>
            <a href="#logos" data-testid="nav-logos">Logos</a>
            <a href="#palette" data-testid="nav-palette">Palette</a>
            <a href="#type" data-testid="nav-type">Type</a>
            <a href="#banners" data-testid="nav-banners">Banners</a>
            <a href="#pod" data-testid="nav-pod">POD</a>
            <a href="#usage" data-testid="nav-usage">Usage</a>
          </div>
          <Btn onClick={handleDownloadAll} variant="gold" testId="hero-download-kit">
            {busy ? "Preparing kit…" : "Download brand kit"}
          </Btn>
        </nav>

        <div className="hero__body">
          <div className="hero__left">
            <p className="eyebrow">BRAND IDENTITY · 2026</p>
            <h1 className="display">
              Where <em>nature</em><br />meets <em>the soul.</em>
            </h1>
            <p className="hero__sub">
              The complete visual system for <strong>Rona Wellness</strong> — a calm,
              warm <em>luxury lifestyle brand</em> for anyone walking toward faith,
              strength, and hope. Walking for wellness · mindful living · spiritual
              self-care.
            </p>
            <div className="hero__pillars">
              <PillarSymbol size={64} />
              <span>{PILLARS}</span>
            </div>
            <div className="hero__cta">
              <Btn onClick={handleDownloadAll} variant="gold" testId="hero-cta-download">
                {busy ? "Preparing kit…" : "Download full brand kit (.zip)"}
              </Btn>
              <Btn onClick={handleDownloadBrief} variant="ghost" testId="hero-cta-brief">
                {briefBusy ? "Building brief…" : "Download designer brief (.pdf)"}
              </Btn>
              <a className="btn btn--ghost" href="#story" data-testid="hero-read-story">
                Read our story →
              </a>
              <a className="btn btn--ghost" href="#logos" data-testid="hero-explore">
                Explore the system →
              </a>
            </div>
          </div>

          <div className="hero__right">
            <div className="hero__logoframe">
              <MainLogo ref={refs.main} size={440} />
            </div>
            <div className="hero__tagline">
              <em>“{TAGLINE}”</em>
            </div>
          </div>
        </div>
      </header>

      {/* ============ STORY ============ */}
      <section id="story" className="section section--cream">
        <div className="section__head">
          <p className="eyebrow">OUR STORY</p>
          <h2>The story of Rona Wellness.</h2>
          <p className="lede">Two stories, one heart. The parent brand — and the concept that began it all.</p>
        </div>

        <div className="stories">
          <article className="story-card story-card--parent" data-testid="story-wellness">
            <p className="eyebrow story-card__eyebrow">01 · THE BRAND</p>
            <h3 className="story-card__title">{STORY_WELLNESS.title}</h3>
            {STORY_WELLNESS.lines.map((line, i) => (
              <p className="story-card__line" key={i}>{line}</p>
            ))}
            <p className="story-card__close">{STORY_WELLNESS.close}</p>
          </article>

          <article className="story-card story-card--concept" data-testid="story-walk">
            <p className="eyebrow story-card__eyebrow">02 · THE CONCEPT</p>
            <h3 className="story-card__title">{STORY_WALK.title}</h3>
            <p className="story-card__intro">{STORY_WALK.intro}</p>
            {STORY_WALK.lines.map((line, i) => (
              <p className="story-card__line" key={i}>{line}</p>
            ))}
            <p className="story-card__close">{STORY_WALK.close}</p>
            <p className="story-card__mantra">{STORY_WALK.mantra}</p>

            <div className="story-card__tips">
              <p className="eyebrow">HEALTHY WALKING TIPS</p>
              <ul>
                {STORY_WALK.tips.map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </div>
          </article>
        </div>
      </section>

      {/* ============ MISSION · VISION · VALUES ============ */}
      <section id="mvv" className="section">
        <div className="section__head">
          <p className="eyebrow">MISSION · VISION · VALUES</p>
          <h2>The compass beneath the brand.</h2>
          <p className="lede">
            Rona Wellness is not simply a product brand. It is a lifestyle movement — grounded in a clear mission, a global vision, and a values compass that guides every design, every product, every walk.
          </p>
        </div>

        <div className="mvv" data-testid="mission-vision-values">
          <div className="mvv__card mvv__card--mission" data-testid="mvv-mission">
            <p className="eyebrow">01 · MISSION</p>
            <h3>Why we walk.</h3>
            <p>{BRAND_MISSION}</p>
          </div>

          <div className="mvv__card mvv__card--vision" data-testid="mvv-vision">
            <p className="eyebrow">02 · VISION</p>
            <h3>Where we walk to.</h3>
            <p>{BRAND_VISION}</p>
          </div>

          <div className="mvv__card mvv__card--values" data-testid="mvv-values">
            <p className="eyebrow">03 · VALUES</p>
            <h3>How we walk.</h3>
            <div className="mvv__chips">
              {BRAND_VALUES.map((v, i) => (
                <span className="mvv__chip" key={i}>{v}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ LOGOS ============ */}
      <section id="logos" className="section">
        <div className="section__head">
          <p className="eyebrow">01 — LOGO SYSTEM</p>
          <h2>Five marks. One voice.</h2>
          <p className="lede">
            A core logo for the brand, a concept logo for The Rona Walk, and supporting
            marks for embroidery, social avatars and subtle product detailing.
          </p>
        </div>

        <div className="grid">
          <AssetCard
            title="Main Logo"
            description="Tree · roots · flowing path · sun glow. For website, storefront covers and product hangtags."
            tall
            actions={
              <>
                <Btn onClick={dlSvg(refs.main, "rona-wellness-main-logo")} testId="dl-svg-main">SVG</Btn>
                <Btn onClick={dlPng(refs.main, "rona-wellness-main-logo", 2000, 2000)} testId="dl-png-main-2000" variant="ghost">PNG · 2000</Btn>
                <Btn onClick={dlPng(refs.main, "rona-wellness-main-logo", 4500, 4500)} testId="dl-png-main-4500" variant="ghost">PNG · 4500</Btn>
              </>
            }
          >
            <div className="stage stage--dark">
              <MainLogo size={340} />
            </div>
          </AssetCard>

          <AssetCard
            title="The Rona Walk"
            description="Concept logo. The ‘L’ becomes a walking woman. Use for series, journals, and movement campaigns."
            tall
            actions={
              <>
                <Btn onClick={dlSvg(refs.walk, "the-rona-walk-logo")} testId="dl-svg-walk">SVG</Btn>
                <Btn onClick={dlPng(refs.walk, "the-rona-walk-logo", 2000, 1050)} testId="dl-png-walk-2000" variant="ghost">PNG · 2000</Btn>
                <Btn onClick={dlPng(refs.walk, "the-rona-walk-logo", 4500, 2362)} testId="dl-png-walk-4500" variant="ghost">PNG · 4500</Btn>
              </>
            }
          >
            <div className="stage stage--cream">
              <RonaWalkLogo ref={refs.walk} size={420} />
            </div>
          </AssetCard>

          <AssetCard
            title="Micro Icon — RW-Feather"
            description="A slender feather framing an elegant flowing RW monogram. Spiritual, lightweight, embroidery-perfect. Social avatars, favicon, label seals."
            actions={
              <>
                <Btn onClick={dlSvg(refs.micro, "micro-icon-rw-feather-sage")} testId="dl-svg-micro">SVG sage</Btn>
                <Btn onClick={dlSvg(refs.microGold, "micro-icon-rw-feather-gold")} testId="dl-svg-micro-gold" variant="ghost">SVG gold</Btn>
                <Btn onClick={dlPng(refs.micro, "micro-icon-rw-feather-sage", 1024, 1024)} testId="dl-png-micro-1024" variant="ghost">PNG · 1024</Btn>
              </>
            }
          >
            <div className="stage stage--cream stage--row">
              <MicroIcon ref={refs.micro} size={150} color={COLORS.sage} />
              <MicroIcon ref={refs.microGold} size={150} color={COLORS.gold} />
            </div>
          </AssetCard>

          <AssetCard
            title="Walk Figure Icon"
            description="The walking woman standalone — extracted from The Rona Walk. Bold silhouette, one color, embroidery-perfect. Ideal for t-shirts, tote bags, chest prints, wall art."
            actions={
              <>
                <Btn onClick={dlSvg(refs.walkFigure, "walk-figure-icon-gold")} testId="dl-svg-walk-figure">SVG gold</Btn>
                <Btn onClick={dlSvg(refs.walkFigureSage, "walk-figure-icon-sage")} testId="dl-svg-walk-figure-sage" variant="ghost">SVG sage</Btn>
                <Btn onClick={dlPng(refs.walkFigure, "walk-figure-icon-gold", 1024, 1024)} testId="dl-png-walk-figure-1024" variant="ghost">PNG · 1024</Btn>
                <Btn onClick={dlPng(refs.walkFigure, "walk-figure-icon-gold", 2000, 2000)} testId="dl-png-walk-figure-2000" variant="ghost">PNG · 2000</Btn>
              </>
            }
          >
            <div className="stage stage--cream stage--row">
              <WalkFigureIcon ref={refs.walkFigure} size={180} color={COLORS.gold} />
              <WalkFigureIcon ref={refs.walkFigureSage} size={180} color={COLORS.sage} />
            </div>
          </AssetCard>

          <AssetCard
            title="Tree Icon"
            description="Simplified Main Logo for embroidery — sage tree with a golden forward-curving path. Two-tone but embroidery-friendly (only 2 thread colors). Perfect for hoodies, mugs, journals, wall art, tote bags. Also comes as tree-only (single color) for tiny sizes like neck tags."
            actions={
              <>
                <Btn onClick={dlSvg(refs.treeIconSage, "tree-sage-goldpath")} testId="dl-svg-tree-sage">SVG sage+gold path</Btn>
                <Btn onClick={dlSvg(refs.treeIconGold, "tree-gold-brownpath")} testId="dl-svg-tree-gold" variant="ghost">SVG gold+brown path</Btn>
                <Btn onClick={dlSvg(refs.treeIconNoPath, "tree-icon-only-sage")} testId="dl-svg-tree-only" variant="ghost">SVG tree only</Btn>
                <Btn onClick={dlPng(refs.treeIconSage, "tree-sage-goldpath", 1024, 1024)} testId="dl-png-tree-1024" variant="ghost">PNG · 1024</Btn>
                <Btn onClick={dlPng(refs.treeIconSage, "tree-sage-goldpath", 2000, 2000)} testId="dl-png-tree-2000" variant="ghost">PNG · 2000</Btn>
                <Btn onClick={dlPng(refs.treeIconSage, "tree-sage-goldpath", 4500, 4500)} testId="dl-png-tree-4500" variant="ghost">PNG · 4500-embroidery</Btn>
              </>
            }
          >
            <div className="stage stage--cream stage--row">
              <TreeIcon ref={refs.treeIconSage} size={170} color={COLORS.sage} pathColor={COLORS.gold} withPath={true} />
              <TreeIcon ref={refs.treeIconGold} size={170} color={COLORS.gold} pathColor={COLORS.brown} withPath={true} />
              <TreeIcon ref={refs.treeIconNoPath} size={170} color={COLORS.sage} withPath={false} />
            </div>
          </AssetCard>

          <AssetCard
            title="RW Signature"
            description="Handwritten monogram. Embroidery-ready. Gold / black / white."
            actions={
              <>
                <Btn onClick={dlSvg(refs.sig, "signature-rw-gold")} testId="dl-svg-sig-gold">SVG gold</Btn>
                <Btn onClick={dlSvg(refs.sigBlack, "signature-rw-black")} testId="dl-svg-sig-black" variant="ghost">SVG black</Btn>
                <Btn onClick={dlSvg(refs.sigWhite, "signature-rw-white")} testId="dl-svg-sig-white" variant="ghost">SVG white</Btn>
                <Btn onClick={dlPng(refs.sig, "signature-rw-gold", 2000, 1111)} testId="dl-png-sig-2000" variant="ghost">PNG · 2000</Btn>
              </>
            }
          >
            <div className="stage stage--cream stage--rows">
              <div className="sig-row"><SignatureRW ref={refs.sig} size={220} color={COLORS.gold} /></div>
              <div className="sig-row"><SignatureRW ref={refs.sigBlack} size={220} color="#0B0B0B" /></div>
              <div className="sig-row sig-row--dark"><SignatureRW ref={refs.sigWhite} size={220} color="#FFFFFF" /></div>
            </div>
          </AssetCard>

          <AssetCard
            title="Pillar Symbol"
            description="Flower · leaf · stem. Faith • Strength • Hope. Sits under the brand name."
            actions={
              <>
                <Btn onClick={dlSvg(refs.pillar, "pillar-symbol-faith-strength-hope")} testId="dl-svg-pillar">SVG</Btn>
                <Btn onClick={dlPng(refs.pillar, "pillar-symbol-faith-strength-hope", 800, 1000)} testId="dl-png-pillar-800" variant="ghost">PNG · 800</Btn>
                <Btn onClick={dlPng(refs.pillar, "pillar-symbol-faith-strength-hope", 1600, 2000)} testId="dl-png-pillar-1600" variant="ghost">PNG · 1600</Btn>
              </>
            }
          >
            <div className="stage stage--cream">
              <div className="pillar-lockup">
                <div className="pillar-lockup__name">Rona Wellness</div>
                <PillarSymbol ref={refs.pillar} size={90} />
                <div className="pillar-lockup__sub">{PILLARS}</div>
              </div>
            </div>
          </AssetCard>

          <AssetCard
            title="The Golden Path"
            description="A winding golden path leading into a soft horizon. Rally mark for the community. Tagline — There is healing in movement · Walk · Hydrate · Thrive."
            tall
            actions={
              <>
                <Btn onClick={dlSvg(refs.goldenPath, "golden-path-logo")} testId="dl-svg-golden">SVG</Btn>
                <Btn onClick={dlPng(refs.goldenPath, "golden-path-logo", 2000, 1250)} testId="dl-png-golden-2000" variant="ghost">PNG · 2000</Btn>
                <Btn onClick={dlPng(refs.goldenPath, "golden-path-logo", 4500, 2812)} testId="dl-png-golden-4500" variant="ghost">PNG · 4500</Btn>
              </>
            }
          >
            <div className="stage stage--cream">
              <GoldenPathLogo ref={refs.goldenPath} size={520} />
            </div>
          </AssetCard>
        </div>
      </section>

      {/* ============ PALETTE ============ */}
      <section id="palette" className="section section--cream">
        <div className="section__head">
          <p className="eyebrow">02 — PALETTE</p>
          <h2>Calm earth, quiet sky.</h2>
          <p className="lede">
            Warm neutrals grounded by sage and soft brown, lifted by gold and a whisper of sun.
          </p>
        </div>
        <div className="palette">
          <Swatch label="Sage Green" hex={COLORS.sage} dark />
          <Swatch label="Warm Sand Beige" hex={COLORS.sandBeige} />
          <Swatch label="Gold" hex={COLORS.gold} dark />
          <Swatch label="Dusty Blue" hex={COLORS.dustyBlue} dark />
          <Swatch label="Soft Brown" hex={COLORS.brown} dark />
          <Swatch label="Cream" hex={COLORS.cream} />
          <Swatch label="Soft Yellow" hex={COLORS.yellow} />
        </div>

        {/* Seasonal Accents */}
        <div className="seasons" data-testid="seasonal-palette">
          <div className="seasons__head">
            <p className="eyebrow">SEASONAL ACCENTS · CAMPAIGN COLORS</p>
            <h3>Rotate the accent. Keep the soul.</h3>
            <p className="lede">
              The core palette stays fixed — this is your identity. Rotate these accents for seasonal campaigns and drops, without ever losing brand recognition.
            </p>
          </div>

          <div className="seasons__grid">
            {SEASONAL_PALETTES.map((season) => (
              <div className="season-card" key={season.id} data-testid={`season-${season.id}`}>
                <div className="season-card__head">
                  <div>
                    <h4>{season.name}</h4>
                    <p className="season-card__sub">{season.subtitle}</p>
                  </div>
                  <span className="season-card__months">{season.months}</span>
                </div>
                <div className="season-card__swatches">
                  {season.accents.map((a, i) => (
                    <div className="season-swatch" key={i}>
                      <div
                        className="season-swatch__tile"
                        style={{ background: a.hex, color: a.dark ? "#fff" : COLORS.ink }}
                      >
                        {a.hex.toUpperCase()}
                      </div>
                      <p className="season-swatch__name">{a.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TYPOGRAPHY ============ */}
      <section id="type" className="section">
        <div className="section__head">
          <p className="eyebrow">03 — TYPOGRAPHY</p>
          <h2>Refined, editorial, feminine.</h2>
        </div>
        <div className="type-grid">
          <div className="type-card" data-testid="type-display">
            <p className="type-card__role">DISPLAY · Cormorant Garamond</p>
            <p className="type-card__sample" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Rona Wellness
            </p>
            <p className="type-card__note">For the brand name, hero headlines and large print moments.</p>
          </div>
          <div className="type-card" data-testid="type-accent">
            <p className="type-card__role">ACCENT · Marcellus</p>
            <p className="type-card__sample" style={{ fontFamily: "'Marcellus', serif", letterSpacing: 6 }}>
              FAITH · STRENGTH · HOPE
            </p>
            <p className="type-card__note">Small caps, taglines, labels, banner details.</p>
          </div>
          <div className="type-card" data-testid="type-script">
            <p className="type-card__role">SCRIPT · Sacramento</p>
            <p className="type-card__sample" style={{ fontFamily: "'Sacramento', cursive", color: COLORS.gold }}>
              where movement meets peace
            </p>
            <p className="type-card__note">Occasional handwritten touches for packaging & cards.</p>
          </div>
        </div>
      </section>

      {/* ============ BANNERS ============ */}
      <section id="banners" className="section section--dusty">
        <div className="section__head">
          <p className="eyebrow">04 — BANNERS</p>
          <h2>Ready for Etsy, Shopify, YouTube, Facebook &amp; Instagram.</h2>
          <p className="lede">Pre-sized SVGs that export as crisp PNGs at the exact platform dimensions.</p>
        </div>

        <div className="banners">
          <div className="banner-card" data-testid="banner-etsy-card">
            <div className="banner-card__label">Etsy shop cover · 3360 × 840</div>
            <div className="banner-card__frame">
              <EtsyBanner ref={refs.etsy} width={1100} />
            </div>
            <div className="banner-card__actions">
              <Btn onClick={dlSvg(refs.etsy, "banner-etsy-3360x840")} testId="dl-svg-etsy">SVG</Btn>
              <Btn onClick={dlPng(refs.etsy, "banner-etsy", 3360, 840)} testId="dl-png-etsy" variant="ghost">PNG · 3360×840</Btn>
            </div>
          </div>

          <div className="banner-card" data-testid="banner-shopify-card">
            <div className="banner-card__label">Shopify hero · 1920 × 1080</div>
            <div className="banner-card__frame">
              <ShopifyHero ref={refs.shopify} width={1100} />
            </div>
            <div className="banner-card__actions">
              <Btn onClick={dlSvg(refs.shopify, "banner-shopify-1920x1080")} testId="dl-svg-shopify">SVG</Btn>
              <Btn onClick={dlPng(refs.shopify, "banner-shopify", 1920, 1080)} testId="dl-png-shopify" variant="ghost">PNG · 1920×1080</Btn>
            </div>
          </div>

          <div className="banner-card" data-testid="banner-youtube-card">
            <div className="banner-card__label">YouTube cover · 2560 × 1440</div>
            <div className="banner-card__frame">
              <YouTubeBanner ref={refs.youtube} width={1100} />
            </div>
            <div className="banner-card__actions">
              <Btn onClick={dlSvg(refs.youtube, "banner-youtube-cover-2560x1440")} testId="dl-svg-youtube">SVG</Btn>
              <Btn onClick={dlPng(refs.youtube, "banner-youtube-cover", 2560, 1440)} testId="dl-png-youtube" variant="ghost">PNG · 2560×1440</Btn>
            </div>
          </div>

          <div className="banner-card" data-testid="banner-youtube-thumb-card">
            <div className="banner-card__label">YouTube thumbnail · 1280 × 720</div>
            <div className="banner-card__frame">
              <YouTubeThumbnail ref={refs.youtubeThumb} width={1100} />
            </div>
            <div className="banner-card__actions">
              <Btn onClick={dlSvg(refs.youtubeThumb, "banner-youtube-thumbnail-1280x720")} testId="dl-svg-yt-thumb">SVG</Btn>
              <Btn onClick={dlPng(refs.youtubeThumb, "banner-youtube-thumbnail", 1280, 720)} testId="dl-png-yt-thumb" variant="ghost">PNG · 1280×720</Btn>
            </div>
          </div>

          <div className="banner-card" data-testid="banner-facebook-card">
            <div className="banner-card__label">Facebook cover · 1640 × 924</div>
            <div className="banner-card__frame">
              <FacebookCover ref={refs.facebook} width={1100} />
            </div>
            <div className="banner-card__actions">
              <Btn onClick={dlSvg(refs.facebook, "banner-facebook-cover-1640x924")} testId="dl-svg-facebook">SVG</Btn>
              <Btn onClick={dlPng(refs.facebook, "banner-facebook-cover", 1640, 924)} testId="dl-png-facebook" variant="ghost">PNG · 1640×924</Btn>
            </div>
          </div>

          <div className="banner-card" data-testid="banner-instagram-card">
            <div className="banner-card__label">Instagram post · 1080 × 1080</div>
            <div className="banner-card__frame banner-card__frame--square">
              <InstagramPost ref={refs.instagram} width={560} />
            </div>
            <div className="banner-card__actions">
              <Btn onClick={dlSvg(refs.instagram, "banner-instagram-post-1080x1080")} testId="dl-svg-instagram">SVG</Btn>
              <Btn onClick={dlPng(refs.instagram, "banner-instagram-post", 1080, 1080)} testId="dl-png-instagram" variant="ghost">PNG · 1080×1080</Btn>
            </div>
          </div>

          <div className="banner-card" data-testid="banner-ig-story-card">
            <div className="banner-card__label">Instagram story · 1080 × 1920</div>
            <div className="banner-card__frame banner-card__frame--vertical">
              <InstagramStory ref={refs.igStory} width={320} />
            </div>
            <div className="banner-card__actions">
              <Btn onClick={dlSvg(refs.igStory, "banner-instagram-story-1080x1920")} testId="dl-svg-ig-story">SVG</Btn>
              <Btn onClick={dlPng(refs.igStory, "banner-instagram-story", 1080, 1920)} testId="dl-png-ig-story" variant="ghost">PNG · 1080×1920</Btn>
            </div>
          </div>
        </div>
      </section>

      {/* ============ POD — Amazon Merch & Etsy ============ */}
      <section id="pod" className="section section--cream">
        <div className="section__head">
          <p className="eyebrow">05 — PRINT ON DEMAND</p>
          <h2>Launch-ready for Etsy &amp; Amazon Merch.</h2>
          <p className="lede">
            Print-shop dimensions, transparent PNGs, no guesswork. Upload directly to Merch on Demand or your Etsy POD partner.
          </p>
        </div>

        <div className="banners">
          <div className="banner-card" data-testid="pod-merch-card">
            <div className="banner-card__label">Amazon Merch tee · 4500 × 5400 · transparent</div>
            <div className="banner-card__frame banner-card__frame--tall">
              <MerchPrint ref={refs.merch} width={240} />
            </div>
            <div className="banner-card__actions">
              <Btn onClick={dlSvg(refs.merch, "pod-amazon-merch-tee-4500x5400")} testId="dl-svg-merch">SVG</Btn>
              <Btn onClick={dlPng(refs.merch, "pod-amazon-merch-tee", 4500, 5400)} testId="dl-png-merch" variant="ghost">PNG · 4500×5400</Btn>
            </div>
          </div>

          <div className="banner-card" data-testid="pod-shop-icon-card">
            <div className="banner-card__label">Etsy / Shopify shop icon · 500 × 500</div>
            <div className="banner-card__frame banner-card__frame--square">
              <ShopIcon ref={refs.shopIcon} width={300} />
            </div>
            <div className="banner-card__actions">
              <Btn onClick={dlSvg(refs.shopIcon, "pod-etsy-shop-icon-500x500")} testId="dl-svg-shop-icon">SVG</Btn>
              <Btn onClick={dlPng(refs.shopIcon, "pod-etsy-shop-icon", 500, 500)} testId="dl-png-shop-icon-500" variant="ghost">PNG · 500</Btn>
              <Btn onClick={dlPng(refs.shopIcon, "pod-etsy-shop-icon", 1000, 1000)} testId="dl-png-shop-icon-1000" variant="ghost">PNG · 1000</Btn>
            </div>
          </div>
        </div>

        {/* Launch Checklist */}
        <div className="checklist" data-testid="launch-checklist">
          <div className="checklist__head">
            <p className="eyebrow">POD LAUNCH CHECKLIST</p>
            <h3>Your first week on Etsy &amp; Amazon Merch.</h3>
          </div>

          <div className="checklist__grid">
            <div className="checklist__col" data-testid="checklist-etsy">
              <div className="checklist__badge">01 · ETSY SHOP</div>
              <ol>
                <li><strong>Register your Etsy shop</strong> — pick name "Rona Wellness" (check availability).</li>
                <li><strong>Shop tagline</strong> — use <em>"Rona Wellness · Walking for Wellness · A Lifestyle Brand"</em> in your Etsy shop title/announcement.</li>
                <li><strong>Upload shop icon</strong> — use <em>pod-etsy-shop-icon-500x500.png</em>.</li>
                <li><strong>Upload shop cover</strong> — use <em>banner-etsy-3360x840.png</em>.</li>
                <li><strong>About section</strong> — paste your Rona Wellness story (from the Story section above). Mention that this is a lifestyle brand rooted in walking meditation, faith, and inclusive community.</li>
                <li><strong>Connect Printful or Printify</strong> (both free). These handle printing & shipping.</li>
                <li><strong>For premium Tapstitch tags</strong> — send them the RW-feather gold SVG. Their tags become your "lifestyle brand" signature detail.</li>
                <li><strong>Upload the main logo SVG/PNG</strong> to Printful's design library.</li>
                <li><strong>Create your first 5 listings</strong>: tee, hoodie, tote, mug, journal. Start with one design — the main logo — across all.</li>
                <li><strong>Write listing titles</strong> like <em>"Walking for Wellness Tee — Faith Strength Hope — Rona Wellness Lifestyle"</em>.</li>
                <li><strong>Use the Instagram post</strong> as your product lifestyle preview.</li>
              </ol>
            </div>

            <div className="checklist__col" data-testid="checklist-merch">
              <div className="checklist__badge">02 · AMAZON MERCH ON DEMAND</div>
              <ol>
                <li><strong>Apply at merch.amazon.com</strong> — approval can take a few days to weeks.</li>
                <li><strong>Once approved</strong>, click <em>Create → Standard t-shirt</em>.</li>
                <li><strong>Upload</strong> <em>pod-amazon-merch-tee-4500x5400.png</em> — it's already their exact spec (transparent, 300 DPI).</li>
                <li><strong>Title</strong>: "Rona Wellness · Walking for Wellness · Faith Strength Hope Lifestyle Tee".</li>
                <li><strong>Bullet 1</strong>: "A calm lifestyle brand for anyone seeking inner strength, peace, and healing through walking meditation."</li>
                <li><strong>Bullet 2</strong>: "Tree of faith · roots of strength · path of hope. For working women, friends, families, and anyone walking toward wellness."</li>
                <li><strong>Keywords</strong>: walking for wellness, lifestyle brand, faith, mindful living, gratitude, morning walk, walking meditation, self-care, friends and family.</li>
                <li><strong>Price</strong>: start at $19.99. You earn ~$2.50–$5 per tee. Stair-step pricing: Merch raises your tier as you sell.</li>
              </ol>
            </div>

            <div className="checklist__col" data-testid="checklist-growth">
              <div className="checklist__badge">03 · GROWTH LOOP</div>
              <ol>
                <li><strong>Post daily on Instagram</strong> using your IG post + story templates — morning walk photos, tagline overlays.</li>
                <li><strong>Pinterest</strong> — each listing becomes 3 pins. Pinterest is traffic gold for wellness + Christian niches.</li>
                <li><strong>Tiktok</strong> — quiet morning-walk videos with your brand overlay. Zero-face content ranks well in this aesthetic.</li>
                <li><strong>Collect emails</strong> on Shopify or Etsy. Offer the free Instagram story template as a welcome gift.</li>
                <li><strong>Target: 10 listings in month 1</strong>. Iterate on what sells. Re-invest revenue toward Ritz at the $1,500+ milestone for a full brand refresh.</li>
                <li><strong>Track weekly</strong>: views, favorites, conversions. POD is a patience game — most shops hit traction at month 3–6.</li>
              </ol>
            </div>
          </div>

          <div className="checklist__note">
            <strong>Important:</strong> Amazon Merch requires a transparent PNG at <em>exactly 4500×5400 px, 300 DPI, sRGB, under 25&nbsp;MB</em>. The file I generated is already in that spec. Download and upload as-is.
          </div>
        </div>
      </section>

      {/* ============ USAGE ============ */}
      <section id="usage" className="section">
        <div className="section__head">
          <p className="eyebrow">06 — USAGE & CARE</p>
          <h2>How to dress the brand.</h2>
        </div>
        <div className="usage">
          <div className="usage__card">
            <h4>Print on Demand</h4>
            <p>Upload the SVG wherever possible (Printful, Printify, Gelato, Tapstitch). For embroidery and woven labels, send the RW-feather gold SVG to your digitizer — the line-art design converts cleanly for embroidery.</p>
          </div>
          <div className="usage__card">
            <h4>Etsy &amp; Shopify</h4>
            <p>Use the Etsy 3360×840 cover and Shopify 1920×1080 hero. Pair the main logo with the Cormorant wordmark; let the pillar symbol punctuate your About page.</p>
          </div>
          <div className="usage__card">
            <h4>Social Media</h4>
            <p>Lead with the Instagram 1080×1080 post for launch. Use the RW-feather micro icon as your profile avatar across platforms for recognition.</p>
          </div>
          <div className="usage__card">
            <h4>Do &amp; Don't</h4>
            <ul>
              <li>Do keep generous negative space around the main logo.</li>
              <li>Do use sage + cream as default pairing; gold is an accent.</li>
              <li>Don't recolor the logo outside the palette.</li>
              <li>Don't stretch or distort — scale proportionally.</li>
            </ul>
          </div>
        </div>

        {/* Tagline hierarchy */}
        <div className="tagline-hierarchy" data-testid="tagline-hierarchy">
          <div className="tagline-hierarchy__head">
            <p className="eyebrow">TAGLINE HIERARCHY</p>
            <h3>Every phrase, its own purpose.</h3>
            <p className="lede">One primary tagline. The others live in specific corners of the brand — never all in the same place.</p>
          </div>

          <div className="tagline-table">
            {TAGLINE_HIERARCHY.map((t, i) => (
              <div className="tagline-row" key={i}>
                <div className="tagline-row__level">
                  <span className="tagline-row__idx">0{i + 1}</span>
                  <span className="tagline-row__meta">{t.level}</span>
                </div>
                <p className="tagline-row__phrase">{t.phrase}</p>
                <p className="tagline-row__where">{t.where}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FOOTER CTA ============ */}
      <footer className="footer">
        <div className="footer__mark">
          <MainLogo size={120} withRing={true} />
        </div>
        <h3>Your complete brand kit — in one click.</h3>
        <p>All logos as SVG + transparent PNG at 2000, 4500 &amp; platform sizes. Ready for Etsy, Shopify, POD and embroidery.</p>
        <Btn onClick={handleDownloadAll} variant="gold" testId="footer-download-kit">
          {busy ? "Preparing kit…" : "Download brand kit (.zip)"}
        </Btn>
        <div style={{ marginTop: 14 }}>
          <Btn onClick={handleDownloadBrief} variant="ghost" testId="footer-download-brief">
            {briefBusy ? "Building brief…" : "Download designer brief (.pdf)"}
          </Btn>
        </div>
        <p className="footer__fine">© Rona Wellness · {PILLARS} · {TAGLINE}</p>
      </footer>
    </div>
  );
}
